import { Segment } from '@dna/analytics'
const registerGlobal = window.DNARegisterGlobal

/**
 * Fetches the product recommendations for a given product.
 *
 * @param {string} productId - The product ID.
 * @param {number} limit - The max number of products to fetch
 * @param {string} intent - The recommendation intent.
 * @returns {Promise<Object>} The processed product data.
 */
export const getProductRecommendations = async (
  productId,
  limit = 10,
  intent = 'related',
) => {
  const response = await fetch(
    `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`,
  )
  const { products } = await response.json()
  return products
}

/**
 * Checks whether a value represents a valid number.
 * @example
 * validateNumber(NaN)       // returns false
 * validateNumber(undefined) // returns false
 * validateNumber(1)         // returns true
 * validateNumber(1.1)       // returns true
 * validateNumber('1')       // returns true
 * validateNumber('1.1')     // returns true
 * validateNumber('$1.00')   // returns false (preformatted price needs a different check)
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - True if value is a valid number, false otherwise.
 */
export const validateNumber = (value) =>
  Number.isFinite(Number.parseFloat(value))

/**
 * Converts number representing a price in minor unit (e.g. cents) to major unit (e.g. dollars).
 *
 * @param {number|string} priceInMinorUnit - The price in minor unit.
 * @returns {number|string|false} - The price in major unit or false.
 */
export function minorToMajorUnit(priceInMinorUnit) {
  if (!validateNumber(priceInMinorUnit)) return false
  return (priceInMinorUnit / 100).toFixed(2)
}

/**
 * Formats a money amount based on currency and locale.
 *
 * @param {number} rawPrice - The money amount formatted as we expect it from liquid.
 * @returns {string} The formatted money amount.
 */
export function renderPrice(
  rawPrice,
  options = {
    includeCurrencySymbol: true,
    convertToUSD: false,
  },
) {
  const getMinorUnits = window.__DNA_GLOBAL__?.currency.getMinorUnits
  const currencyCode = Shopify?.currency?.active || 'USD'
  const locale = Shopify?.locale || 'en-US'
  let numericPrice = rawPrice

  // When non-number price received
  if (typeof rawPrice === 'string') {
    numericPrice = Number(rawPrice.replaceAll(/[^0-9,\.]/g, ''))
    if (Number.isNaN(numericPrice)) {
      console.warn(`Could not render price ${rawPrice}`)
    }
  }

  // Convert to USD if needed (originally implemented for Constructor)
  if (options.convertToUSD) {
    const convertedPrice = numericPrice / window.Shopify.currency.rate
    numericPrice = Number(convertedPrice.toFixed())
  }

  // Account for Shopify appending tenths/hundredths to currencies without minor units.
  // https://shopify.dev/docs/api/liquid/objects/line_item#line_item-original_price
  const noSubunits = window.__DNA_GLOBAL__?.currency.no_subunits
  if (noSubunits) {
    numericPrice /= 100
  }

  // Format the amount based on the locale
  const normalizedPrice = numericPrice / 10 ** getMinorUnits(currencyCode)

  // Set options
  const formatterOptions = {}
  if (options.includeCurrencySymbol) {
    formatterOptions.style = 'currency'
    formatterOptions.currency = currencyCode
    formatterOptions.currencyDisplay = 'narrowSymbol'
  }

  // Drop trailing zeroes (.00)
  if (normalizedPrice % 1 === 0) {
    formatterOptions.minimumFractionDigits = 0
    formatterOptions.maximumFractionDigits = 0
  }

  return createPriceFormatter(locale, formatterOptions).format(normalizedPrice)
}

function createPriceFormatter(locale, options) {
  return new Intl.NumberFormat(locale, options)
}

/**
 * Converts string in snake case to camel case.
 *
 * @param {string} string - String to convert.
 * @returns {string} - Converted string in camel case.
 */
export function snakeToCamel(string) {
  return string.replace(/(_\w)/g, (matches) => matches[1].toUpperCase())
}

/**
 * Removes all text from a string before
 *
 * @param {string} source - Source.
 * @param {string} character- Character to remove text before.
 * @returns {string} - Removes all text before first occurrence of character and trims the result
 */
export function removeTextBeforeCharacterAndTrim(source, character) {
  if (typeof source !== 'string') {
    return ''
  }

  const index = source.indexOf(character)
  return index === -1 ? source : source.slice(index + 1).trim()
}

/**
 * Simple debounce function that prevents excessive function calls.
 *
 * @param {Function} func - Function to debounce.
 * @param {number} delay - Interval within which call cannot be re-executed.
 * @returns {Function} - Debounced function.
 */
export function debounce(func, delay) {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

/**
 * Checks if a given string represents a valid stringified array.
 *
 * @param {string} string - The string to check.
 * @returns {boolean} - Returns true if the string is a valid stringified array, otherwise false.
 */
export function isStringifiedArray(string) {
  if (typeof string !== 'string') return false
  if (string.startsWith('[') && string.endsWith(']')) {
    try {
      const potentialArray = JSON.parse(string)
      return Array.isArray(potentialArray)
    } catch (error) {
      return false
    }
  }
  return false
}

/**
 * Opens a drawer with the specified ID by dispatching a custom event.
 *
 * @param {string} id - The ID of the drawer to open.
 * @fires CustomEvent#dna:drawer:open
 */
export function openDrawer(id) {
  document.dispatchEvent(new CustomEvent('dna:drawer:open', { detail: { id } }))
}

/**
 * Escapes special characters in the given HTML string to prevent XSS attacks.
 *
 * @param {string} html - The HTML string to escape.
 * @returns {string} The escaped HTML string.
 */
export function escapeHtml(html) {
  const div = document.createElement('div')
  div.textContent = html
  return div.innerHTML
}

/**
 * Displays a toast using the Shoelace alert component with the specified message, variant, icon, and duration.
 *
 * @param {string} message - The message to display in the alert.
 * @param {string} [variant='primary'] - The variant of the alert (e.g., 'primary', 'success', 'warning', 'danger').
 * @param {number} [duration=3000] - The duration (in milliseconds) for which the alert will be displayed.
 * @returns {Promise} A Promise that resolves when the alert is dismissed.
 */
export function notify(message, variant = 'primary', duration = 3000) {
  const alert = Object.assign(document.createElement('sl-alert'), {
    variant,
    closable: true,
    duration: duration,
    innerHTML: `
      ${escapeHtml(message)}
    `,
  })

  document.body.append(alert)
  return alert.toast()
}

window.notify = notify

/**
 * Adds one or more product variants to the Shopify cart.
 *
 * @async
 * @function
 * @param {Array|Object} items - An array of objects or a single object representing the items to add to the cart.
 * Each object should have an `id` property (variant ID) and a `quantity` property.
 * @param {boolean} [openCart=true] - Whether to trigger the cart to open after adding items.
 * @param {boolean} [notify=true] - Whether to show notification to user on failure
 * @throws Will throw an error if the fetch operation encounters a problem.
 * @example
 * addToCart({ id: '1234567890', quantity: 1 });
 * addToCart([{ id: '1234567890', quantity: 1 }, { id: '0987654321', quantity: 2 }]);
 */
export const addToCart = async (
  _items,
  openCart = true,
  notifyFailure = true,
) => {
  let items = _items
  if (!Array.isArray(items)) {
    items = [items]
  }
  try {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    })
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
    // Update cart status message for screen readers
    const $cartStatusMessage = document.querySelector('.cart-status-message')
    if ($cartStatusMessage) {
      $cartStatusMessage.textContent = 'Item added to cart'
      setTimeout(() => {
        $cartStatusMessage.textContent = ''
      }, 5000)
    }
    // Get cart json again as added response includes only new item amount
    const cartResult = await fetch(`${window.Shopify.routes.root}cart.js`)
    const detail = await cartResult.json()
    document.dispatchEvent(
      new CustomEvent('dna:cart:refresh', {
        bubbles: true,
        composed: true,
        detail,
      }),
    )
    const metadataWarning =
      'A product was added to the cart with incomplete metadata. There may be missing information visible in the cart or in analytics events.'
    for (const item of items) {
      if (!item.properties || !item.properties._storefront_category) {
        console.warn(metadataWarning)
        break
      }
    }
    if (openCart) {
      document.dispatchEvent(
        new CustomEvent('dna:cart:open', { bubbles: true, detail: response }),
      )
    }
    Segment.protect(() => {
      if (detail.items.length) {
        for (const cartItem of detail.items) {
          for (const addedItem of items) {
            if (addedItem.id && cartItem.id) {
              const addedItemId = addedItem.id.toString().toLowerCase().trim()
              const cartItemId = cartItem.id.toString().toLowerCase().trim()
              if (addedItemId === cartItemId) {
                const addToCartAnalyticsData = {
                  brand: Segment.CONSTANTS.BRAND,
                  cart_id: Segment.cartToken,
                  category: splitProductName(cartItem.product_title).style,
                  currency: Segment.presentmentCurrency,
                  image_url: cartItem.featured_image.url,
                  name: splitProductName(cartItem.product_title).color,
                  price: Number(minorToMajorUnit(cartItem.price)),
                  product_category: cartItem.properties._storefront_category
                    ? cartItem.properties._storefront_category
                    : cartItem.tags
                      ? getCategoryFromTags(cartItem.tags)
                      : undefined,
                  product_id: cartItem.product_id.toString(),
                  quantity: 1,
                  shopify_product_id: cartItem.product_id.toString(),
                  shopify_variant_id: cartItem.variant_id.toString(),
                  sku: cartItem.sku,
                  sku_short: getMasterSku(cartItem.sku),
                  url: `${window.location.origin}/products/${cartItem.handle}`,
                  variant: cartItem.variant_title,
                }

                // @gtm
                Segment.dataLayerPush(
                  Segment.CONSTANTS.EVENTS.PRODUCT_ADDED,
                  addToCartAnalyticsData,
                )

                // @intelligems
                window.igEvents = window.igEvents || []
                window.igEvents.push({
                  event: Segment.CONSTANTS.EVENTS.PRODUCT_ADDED,
                  properties: addToCartAnalyticsData,
                })
              }
            }
          }
        }
      }
    })
  } catch (error) {
    console.error(error.message)
    if (notifyFailure) {
      notify('Failed to add to cart.', 'danger')
    }
  }
}

/**
 * Update the cart's line item quantities
 *
 * @async
 * @function
 * @param {Array|Object} items - An array of objects or a single object representing the items to update
 * Each object should have an `id` property (variant ID or line item key) and a `quantity` property.
 * @param {boolean} [openCart=true] Whether to trigger the cart to open after updating.
 * @throws Will throw an error if the fetch operation encounters a problem.
 * @example
 * updateCartQuantity({ id: '1234567890', quantity: 1 });
 * updateCartQuantity([{ id: '1234567890', quantity: 1 }, { id: '0987654321', quantity: 2 }]);
 */
export async function updateCartQuantity(_items, openCart = true) {
  let items = _items
  if (!Array.isArray(items)) {
    items = [items]
  }

  try {
    const updates = {}
    for (const item of items) {
      updates[item.id] = item.quantity
    }
    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ updates }),
    })

    if (!response.ok) {
      throw new Error('Error updating quantity in cart')
    }
    if (openCart) {
      document.dispatchEvent(
        new CustomEvent('dna:cart:open', {
          bubbles: true,
          composed: true,
          detail: response,
        }),
      )
    }
    const detail = await response.json()
    document.dispatchEvent(
      new CustomEvent('dna:cart:refresh', {
        bubbles: true,
        composed: true,
        detail,
      }),
    )
  } catch (error) {
    console.error(error.message)
    throw error
  }
}
/**
 * Interpolates a template string with provided replacements.
 *
 * @param {string} template - The string template with placeholders.
 * @param {Object} replacements - An object containing key-value pairs for placeholders and their replacements.
 * @returns {string} The interpolated string.
 *
 * @example
 * const messageTemplate = 'Hello, {{name}}! You have {{count}} messages.';
 * const values = {
 *   name: 'Alice',
 *   count: 5
 * }
 * const message = interpolate(messageTemplate, values);
 * console.log(message); // "Hello, Alice! You have 5 messages."
 */
export function interpolate(template, replacements) {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
    return replacements[placeholder] || _
  })
}

/**
 * Finds and returns a slotted child within a Web Component
 *
 * @param {DOMElement} shadowRoot
 * @param {String} name
 * @param {Boolean} firstOnly
 * @returns DOMElement | Empty
 */
export function getChildSlot(shadowRoot = null, name = '', firstOnly = false) {
  const children =
    shadowRoot
      ?.querySelector('slot')
      ?.assignedElements({ flatten: true })
      .filter((node) => {
        return node.matches(name)
      }) || []
  return firstOnly ? (children || [])[0] : children
}

/**
 * A shorthand for querySelector
 *
 * @param {String} selector
 * @param {DomElement} parent
 * @returns DOMElement | Empty
 */
export function one(selector, _parent) {
  let parent = _parent
  if (parent === null || parent === undefined) {
    parent = document
  }
  return parent.querySelector(selector)
}

/**
 * A shorthand for querySelectorAll
 *
 * @param {String} selector
 * @param {DomElement} parent
 * @returns DOMElement | Empty
 */
export function all(selector, _parent) {
  let parent = _parent
  if (parent === null || parent === undefined) {
    parent = document
  }
  const selection = parent.querySelectorAll(selector)
  return Array.prototype.slice.call(selection)
}

/**
 * Toggles an array of classes on a DOMElement
 *
 * @param {DOMElement} element
 * @param {Array} classes
 */
export function toggle(element, classes = []) {
  for (_class of classes) {
    element.classList.toggle(_class)
  }
}

/**
 * Not lovely & not pure; attempts to pause all currently
 * playing media (ie. videos) TODO: Update to work with new
 * media-chrome based videos.
 */
export const pauseAllMedia = () => {
  const $youTubeVideos = document.querySelectorAll('.js-youtube')
  for (const $video of $youTubeVideos) {
    $video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*',
    )
  }
  const $vimeoVideos = document.querySelectorAll('.js-vimeo')
  for (const $video of $vimeoVideos) {
    $video.contentWindow.postMessage('{"method":"pause"}', '*')
  }
  const $videos = document.querySelectorAll('video')
  for (const $video of $videos) {
    $video.pause()
  }
  const $models = document.querySelectorAll('product-model')
  for (const $model of $models) {
    $model.modelViewerUI?.pause()
  }
}

/**
 * A classic throttle helper
 *
 * @param {Function} callback
 * @param {Number} limit
 * @returns
 */
export const throttle = (callback, limit) => {
  let waiting = false
  return function (...args) {
    if (!waiting) {
      callback.apply(this, args)
      waiting = true
      setTimeout(() => {
        waiting = false
      }, limit)
    }
  }
}

/**
 * Serializes form
 *
 * @param {Form} form
 * @returns String
 */
export const serializeForm = (form) => {
  const obj = {}
  const formData = new FormData(form)

  for (const key of formData.keys()) {
    // ensures that the line item properties are added as an object, instead of as an array
    const regex = /(?:^(properties\[))(.*?)(?:\]$)/

    if (regex.test(key)) {
      obj.properties = obj.properties || {}
      obj.properties[regex.exec(key)[2]] = formData.get(key)
    } else {
      obj[key] = formData.get(key)
    }
  }

  return JSON.stringify(obj)
}

/**
 * Returns a generic fetch config object (XHR)
 *
 * @param {String} type
 * @param {String} method
 * @returns Object
 */
export const fetchConfig = (type = 'json', method = 'POST') => {
  return {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  }
}

/**
 * Checks to see if the device supports touch gestures.
 */
export const isTouch = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints
}

/**
 * ViewportObserver utility class.
 * Allows Lit components to be aware of the viewport size and respond to changes.
 */
class ViewportObserver {
  constructor() {
    this.listeners = []
    this.mediaQueries = {
      mobile: window.matchMedia('(max-width: 768px)'),
      tablet: window.matchMedia('(min-width: 768px) and (max-width: 1024px)'),
      desktop: window.matchMedia('(min-width: 1024px) and (max-width: 1440px)'),
      widescreen: window.matchMedia('(min-width: 1440px)'),
    }

    this.currentViewport = this.getCurrentViewport()

    // Attach event listeners for media query changes
    for (const mediaQuery of Object.values(this.mediaQueries)) {
      mediaQuery.addEventListener('change', this._handleMediaQueryChange)
    }
  }

  /**
   * Determines the current viewport based on the defined media queries.
   * @returns {string} The current viewport size
   */
  getCurrentViewport() {
    if (this.mediaQueries.mobile.matches) {
      return 'mobile'
    }
    if (this.mediaQueries.tablet.matches) {
      return 'tablet'
    }
    if (this.mediaQueries.desktop.matches) {
      return 'desktop'
    }
    if (this.mediaQueries.widescreen.matches) {
      return 'widescreen'
    }
  }

  /**
   * Event handler for media query changes.
   * Updates the current viewport and notifies the registered listeners.
   */
  _handleMediaQueryChange = () => {
    const newViewport = this.getCurrentViewport()
    if (newViewport !== this.currentViewport) {
      this.currentViewport = newViewport
      this.notifyListeners()
    }
  }

  /**
   * Registers a listener to be notified of viewport changes.
   * @param {function} listener - The listener function to be called when the viewport changes.
   */
  addListener(listener) {
    this.listeners.push(listener)
  }

  /**
   * Removes a previously registered listener.
   * @param {function} listener - The listener function to be removed.
   */
  removeListener(listener) {
    this.listeners = this.listeners.filter(
      (_listener) => _listener !== listener,
    )
  }

  /**
   * Notifies all registered listeners of the current viewport.
   */
  notifyListeners() {
    for (const listener of this.listeners) {
      listener(this.currentViewport)
    }
  }
}

// Create a singleton instance of ViewportObserver
export const viewportObserver = new ViewportObserver()

/**
 * Clones a complex Javascript object. This is a
 * thorough way to ensure that the object is fully
 * cloned with no references kept in place.
 *
 * @param {Object} obj
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * Removes whitespace within a string and makes all
 * characters lowercase.
 *
 * See: https://gist.github.com/pablogiralt/f52d27428e7501909616d1dea04edfeb?permalink_comment_id=4879569#gistcomment-4879569
 *
 * @param {String} str
 */
export function handleize(str) {
  return (
    str
      .normalize('NFD')
      .replace(/[\[\]'()"]+/g, '') // Remove apostrophes, square brackets, and other bits and pieces
      // biome-ignore lint/suspicious/noMisleadingCharacterClass: we are trying to strip these
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/([^\w]+|\s+)/g, '-') // Replace space and other characters by hyphen
      .replace(/\-\-+/g, '-') // Replaces multiple hyphens by one hyphen
      .replace(/(^-+|-+$)/g, '') // Remove extra hyphens from beginning or end of the string
      .toLowerCase() // To lowercase
  )
}

/**
 * Decodes a string that has been encoded through the 'url_encode' Shopify filter
 * @param {*} str
 */
export const decode = (str) => decodeURIComponent(str).replace(/\+/g, ' ')

/**
 * Returns an iFrame src URL value for either YouTube or Vimeo hosted videos
 *
 * @param {string} host 'youtube' or 'vimeo'
 * @param {string} id the external video ID
 * @param {boolean} autoplay whether the video should autoplay
 * @param {boolean} controls whether the video should show controls
 * @param {boolean} loop whether the video should loop
 */
export const externalVideoEmbedSrc = (
  host,
  id,
  autoplay = false,
  controls = false,
  loop = false,
) => {
  let src = ''
  const queryParam = (param, value) => {
    return `${param}=${Number(value)}`
  }
  const autoplayParam = queryParam('autoplay', autoplay)
  const controlsParam = queryParam('controls', controls)
  const loopParam = queryParam('loop', loop)
  const queryParams = `${autoplayParam}&${controlsParam}&${loopParam}`
  let muted

  switch (host) {
    case 'youtube':
      muted = 'mute=1&'
      src = `https://www.youtube.com/embed/${id}?${queryParams}&enablejsapi=1&modestbranding=1&${muted}cc_load_policy=0&disablekb=0&fs=1&iv_load_policy=1&playsinline=1&rel=1&widgetid=1`
      break
    case 'vimeo':
      muted = 'muted=1&'
      src = `https://player.vimeo.com/video/${id}?${queryParams}&${muted}byline=0&playsinline=1&title=0`
      break
    default:
      console.error('Invalid host value (must be "youtube" or "vimeo")')
  }
  return src
}

/**
 * Gets the previous sibling of an element that matches a given selector
 *
 * @param {HTMLElement} element the element to start from
 * @param {string} selector the selector to match
 */
export const getPreviousSibling = (element, selector) => {
  let sibling = element.previousElementSibling

  if (!selector) return null

  while (sibling) {
    if (sibling.matches(selector)) {
      return sibling
    }
    sibling = sibling.previousElementSibling
  }
}

/**
 * A utility function for retrieving localized translation strings in JavaScript.
 * It looks up the translation string for the specified namespace in the `window.langMessages` object.
 * If the translation string is found, it is returned. Otherwise, the provided fallback string is returned.
 *
 * @param {string} namespace - The namespace or key of the translation string.
 * @param {string} fallback - The fallback string to be returned if the translation string is not found.
 * @returns {string} The localized translation string or the fallback string.
 *
 * @example
 * // Assuming `window.langMessages` contains the following translations:
 * // {
 * //   "greeting": "Hello",
 * //   "farewell": "Goodbye"
 * // }
 *
 * const translatedGreeting = t("greeting", "Default greeting");
 * console.log(translatedGreeting); // Output: "Hello"
 *
 * const translatedFarewell = t("farewell", "Default farewell");
 * console.log(translatedFarewell); // Output: "Goodbye"
 *
 * const translatedUnknown = t("unknown", "Default unknown");
 * console.log(translatedUnknown); // Output: "Default unknown"
 */
export const t = (namespace, fallback) => {
  return window.langMessages[namespace]
    ? window.langMessages[namespace]
    : fallback
}

/**
 * A utility class for updating HTML elements with new content using view transitions.
 * It provides methods to add pre-process and post-process callbacks, and a `viewTransition` method
 * to perform the HTML update with a smooth transition effect.
 */
export class HTMLUpdateUtility {
  #preProcessCallbacks = []
  #postProcessCallbacks = []

  /**
   * Adds a pre-process callback function to be executed before the HTML update.
   * @param {Function} callback - The callback function to be added.
   */
  addPreProcessCallback(callback) {
    this.#preProcessCallbacks.push(callback)
  }

  /**
   * Adds a post-process callback function to be executed after the HTML update.
   * @param {Function} callback - The callback function to be added.
   */
  addPostProcessCallback(callback) {
    this.#postProcessCallbacks.push(callback)
  }

  /**
   * Performs a view transition to update an HTML node with new content.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden,
   * and then the old node is removed after a short delay.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a
   * view transition once it is more widely supported.
   * See: https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   *
   * @param {HTMLElement} oldNode - The old HTML node to be replaced.
   * @param {HTMLElement} newContent - The new content to be inserted.
   */
  viewTransition(oldNode, newContent) {
    for (const callback of this.#preProcessCallbacks) {
      callback(newContent)
    }
    const newNode = oldNode.cloneNode()
    HTMLUpdateUtility.setInnerHTML(newNode, newContent.innerHTML)
    oldNode.parentNode.insertBefore(newNode, oldNode)
    oldNode.style.display = 'none'

    for (const callback of this.#postProcessCallbacks) {
      callback(newNode)
    }

    setTimeout(() => oldNode.remove(), 1000)
  }

  /**
   * Sets the inner HTML of an element and re-injects the script tags to allow execution.
   * By default, scripts are disabled when using `element.innerHTML`.
   *
   * @param {HTMLElement} element - The element to set the inner HTML on.
   * @param {string} html - The HTML content to be set.
   */
  static setInnerHTML(element, html) {
    element.innerHTML = html
    const $scripts = element.querySelectorAll('script')
    for (const $oldScriptTag of $scripts) {
      const $newScriptTag = document.createElement('script')
      for (const attribute of Array.from($oldScriptTag.attributes)) {
        $newScriptTag.setAttribute(attribute.name, attribute.value)
      }
      $newScriptTag.appendChild(
        document.createTextNode($oldScriptTag.innerHTML),
      )
      $oldScriptTag.parentNode.replaceChild($newScriptTag, $oldScriptTag)
    }
  }
}

/**
 * Splits product title into style and color.
 * Rothys site admins need a reference to both base product and the given style in the title
 * The agreed format is <Base product> - <Style name> e.g. The Knot Point II - Black
 *
 * @param {string} title - The product title to split.
 */
export function splitProductName(title) {
  if (!title || typeof title !== 'string') {
    return {
      style: '',
      color: '',
    }
  }
  const titleArray = title.split('-')
  const style = titleArray[0]?.trim() || ''
  const color = titleArray[1]?.trim() || ''
  return {
    style,
    color,
  }
}

/**
 * This returned string will have the size removed
 * from the variant SKU for Segment.
 *
 * @param {string} sku - A variant SKU.
 */
export function getMasterSku(sku = '') {
  if (typeof sku !== 'string') {
    return
  }
  const elements = sku.split('_')
  elements.pop()
  return elements.join('_')
}

/**
 * Gets the top-level product category from a product's tags.
 *
 * @param {*} tags - A product's tags.
 */
export function getCategoryFromTags(tags = []) {
  const result = tags.find((tag) => tag.includes('storefront:category:'))
  return result ? result.split('storefront:category:')[1] : false
}

/**
 * Filters an object by keeping only the keys that appear in the specified array.
 *
 * @param {Object} obj - The object to be filtered.
 * @param {string[]} keysToKeep - An array of keys to keep in the filtered object.
 * @returns {Object} A new object containing only the specified keys and their corresponding values.
 */
export function filterObjectKeys(obj, keysToKeep) {
  const keysSet = new Set(keysToKeep)
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => keysSet.has(key)),
  )
}

/**
 * Loads Gladly chat instance (from Gladly documentation) and includes segment event for chat clicked
 *
 * @param {string} context - Context for Segment call depending on where chat icon was clicked
 * @param {string[]} keysToKeep
 */

export function loadChat(context) {
  Segment.protect(
    Segment.track(Segment.CONSTANTS.EVENTS.CHAT_CLICKED, {
      chat_context: context,
    }),
  )

  if (typeof Gladly !== 'undefined') {
    Gladly.show()
  } else {
    const waitForGladly = ({ key, sub }, callback) => {
      if (window[key]) {
        if (sub && window[key][sub]) {
          setTimeout(() => {
            waitForGladly({ key, sub }, callback)
          }, 100)
        } else {
          callback()
        }
      } else {
        setTimeout(() => {
          waitForGladly({ key, sub }, callback)
        }, 100)
      }
    }

    waitForGladly({ key: 'Gladly', sub: 'show' }, () => {
      Gladly.show()
    })
  }
}

/**
 * Given a number, returns it as a string. When the
 * number is a single digit, a leading zero is added.
 *
 * @param {Number} number
 */
export function addLeadingZero(number) {
  if (number < 10 && number >= 0) {
    return number.toString().padStart(2, '0')
  }
  return number.toString()
}

/**
 * Returns a formatted date that meets Iterable's requirements
 * yyyy-MM-dd HH:mm:ss ZZ
 */
export function formattedIterableDate() {
  const date = new Date()
  const utcYear = date.getUTCFullYear().toString()
  const utcMonth = addLeadingZero(date.getUTCMonth() + 1)
  const utcDay = addLeadingZero(date.getUTCDate())
  const utcHours = addLeadingZero(date.getUTCHours())
  const utcMinutes = addLeadingZero(date.getUTCMinutes())
  const utcSeconds = addLeadingZero(date.getUTCSeconds())
  const utcZone = '+00:00'

  return `${utcYear}-${utcMonth}-${utcDay} ${utcHours}:${utcMinutes}:${utcSeconds} ${utcZone}`
}

/**
 * Given product and variant data metafields,
 * returns cart line item properties,
 * maximum purchase limit, preorder/backorder experience, productTags
 *
 * @param {Object} product
 * @param {Object} variant
 */
export function getLineItemProperties(product, variant, properties) {
  let lineItemProperties = {}
  if (properties) {
    const entries = new Map(properties)
    lineItemProperties = Object.fromEntries(entries)
  } else {
    // Set final-sale/exchange-only
    const returnExperience = product.metafields?.rothys?.return_rules
    if (returnExperience) {
      lineItemProperties._LPROP = returnExperience
    }

    // Set preorder/backorder experience
    const preorderMessage =
      product.metafields?.rothys?.preorder_backorder_experience ||
      variant?.metafields?.rothys?.preorder_backorder_experience
    const preorderShipDate =
      product.metafields?.rothys?.estimated_ship_date ||
      variant?.metafields?.rothys?.estimated_ship_date
    if (preorderMessage) {
      lineItemProperties._preorder_message = preorderMessage
      lineItemProperties._preorder_ship_date = preorderShipDate
    }

    // Set maximum product limit
    if (!__DNA_GLOBAL__.customerB2B) {
      const parsedMetafieldValue = Number.parseInt(
        product?.metafields?.rothys?.maximum_order_quantity,
        10,
      )
      lineItemProperties._limit = !Number.isNaN(parsedMetafieldValue)
        ? parsedMetafieldValue
        : __DNA_GLOBAL__.b2cMaxOrderQuantity
    }

    // Set product tags
    if (product.tags?.length) {
      // Get a tag value and attach it individually to properties
      const storefrontCategory = getCategoryFromTags(product.tags)
      if (storefrontCategory)
        lineItemProperties._storefront_category = storefrontCategory
    }

    if (variant?.variantMedia) {
      lineItemProperties._gift_card_image = variant.variantMedia[0].url
    }
  }

  return lineItemProperties
}

export function getUniqueSizeMappingRegions(metaobjects) {
  const sizeMappingRegions = metaobjects.map((metaobject) => {
    return metaobject?.fields?.size_mapping_region ?? null
  })
  const uniqueSizeMappingRegions = [
    ...new Set(sizeMappingRegions.filter(Boolean)),
  ]
  return uniqueSizeMappingRegions
}

export function buildSizeMap(metaobjects, countries) {
  // Select which value to index by based on active market region
  // on initial load, we are setting the current country to be 'US' until we are fully ready to translate all sizes
  // We need to remove this and add the below line back once that happens - also in product-variant-picker line 415
  // const currentCountry = window.Shopify?.country?.toLowerCase()
  const currentCountry = 'us'
  const region = countries[currentCountry]?.fields.size_mapping_region || 'US'

  return metaobjects.reduce((acc, item) => {
    const { fields } = item
    const { type } = fields
    const system = type.toLowerCase()
    const sizingKeyRegex = /[a-z]{2}_size/
    const map = Object.keys(fields)
      .filter((key) => key.match(sizingKeyRegex))
      .reduce((acc, key) => {
        acc[key.split('_')[0].toUpperCase()] = Number.parseFloat(fields[key])
        return acc
      }, {})

    if (!acc[system]) {
      acc[system] = {}
    }

    // Sizes are keyed by the default country size and map to the table of alternate sizes
    const key = __DNA_GLOBAL__.enableLocalSizingDropDown ? map[region] : map.US // Default to US if dropdown is disabled

    acc[system][key] = map
    return acc
  }, {})
}

export function richTextToHTML(content) {
  const parsedContent = JSON.parse(content)
  let html = ''
  for (const node of parsedContent.children) {
    switch (node.type) {
      case 'heading':
        html += `<h${node.level}>${node.children[0].value}</h${node.level}>`
        break
      case 'list':
        html += `<${node.listType === 'unordered' ? 'ul' : 'ol'}>`
        for (const item of node.children) {
          html += `<li>${item.children[0].value}</li>`
        }
        html += `<${node.listType === 'unordered' ? '/ul' : '/ol'}>`
        break
      case 'paragraph':
        html += '<p>'
        for (const item of node.children) {
          if (item.type === 'text' && item.bold) {
            html += `<strong>${item.value}</strong>`
          } else if (item.type === 'text' && item.italic) {
            html += `<em>${item.value}</em>`
          } else if (item.type === 'text') {
            html += `${item.value} `
          }
          if (item.type === 'link' && item.bold) {
            html += `<a href="${item.url}" target="${item.target}"><strong>${item.children[0].value}</strong></a>`
          } else if (item.type === 'link' && item.italic) {
            html += `<a href="${item.url}" target="${item.target}"><em>${item.children[0].value}</em></a>`
          } else if (item.type === 'link') {
            html += `<a href="${item.url}" target="${item.target}">${item.children[0].value}</a>`
          }
        }
        html += '</p>'
        break
    }
  }
  return html
}

/**
 * Resolves a promise if Intelligems has loaded.
 *
 */
export const igReady = new Promise((res) => {
  // In intelligems bundle script there is a chance that this runs
  // when they have initialized the igData to an empty object,
  // but it hasn't completely loaded yet, checking for the getVersion function
  // ensures it's been populated with something.
  if (window.igData && typeof window.igData.getVersion === 'function') {
    res(window.igData)
  } else {
    window.addEventListener('ig:ready', () => {
      res(window.igData)
    })
  }
})

/**
 * Given an Intelligems experiment ID,
 * returns whether the user is in the control group for that experiment.
 *
 * @param {Object} experimentIds
 */
export async function igGetExperimentControl(experimentIds = {}) {
  const igData = await igReady
  const experimentId =
    window.location.hostname === 'rothys.com'
      ? experimentIds.production
      : experimentIds.staging
  return igData.user?.getVariation(experimentId)?.isControl ?? true
}

/**
 * Given an Intelligems experiment ID,
 * returns the name of the group (if there are more than 2 variations)
 *
 * @param {String} experimentIds
 */
export async function igGetExperimentVariationName(experimentIds = {}) {
  const igData = await igReady
  const experimentId =
    window.location.hostname === 'rothys.com'
      ? experimentIds.production
      : experimentIds.staging
  return igData.user?.getVariation(experimentId)?.name
}

/* Given an element (not a selector) that is a Shopify section with
 * class '.shopify-section', returns the position of that section
 * among all Shopify sections in <main>.
 *
 * @param {DOMElement} section
 */
export function getSectionPosition(section) {
  if (!section) return
  const $sections = document.querySelectorAll('#main .shopify-section')
  return Array.from($sections).indexOf(section) + 1 || null
}

let loadedBadges
export async function getBadgeMetaobject() {
  if (!loadedBadges) {
    loadedBadges =
      __DNA_GLOBAL__.StorefrontClient.GetAllMetaobjectsByType('badges')
  }
  return loadedBadges
}

// Expose critical dependencies to the global namespace for use outside of JS modules
registerGlobal(splitProductName, 'splitProductName', 'utils')
registerGlobal(renderPrice, 'renderPrice', 'utils')
registerGlobal(formattedIterableDate, 'formattedIterableDate', 'utils')
registerGlobal(getSectionPosition, 'getSectionPosition', 'utils')
registerGlobal(notify, 'notify', 'utils')
